<template>
  <teleport to="body">
    <div class="modal-container" v-if="state.isVisible">
      <div class="modal" :class="[attrs.class, { 'is-single-row': state.swiperParams.rows === 1 }]" ref="modalBlock">
        <div id="lots-catalog-modal" class="site-tabs lots-catalog">
          <div class="catalog-header">
            <div class="lots-tabs">
              <div
                v-for="tab in tabs"
                class="type-lots-tab-title tab-title"
                :class="{ active: activeItem === tab.key }"
                data-target="lot-tab-content-1"
                @click="activeItem = tab.key"
                :key="tab.key">
                {{ trans(tab.name) }}
              </div>
            </div>
            <div class="lots-controllers stage-b-item">
              <filter-search class="search-block">
                <svg class="icon-search">
                  <use href="/sprite.svg#search"></use>
                </svg>
              </filter-search>
            </div>
            <div class="modal__close" @click="close"></div>
          </div>

          <div class="modal-content">
            <div class="all-lots-block tabs-content">
              <div v-for="tab in tabs" class="tab-content" :class="{ active: tab.key === activeItem }" :key="tab.key">
                <swiper
                  :id="tab.key"
                  class="lots-list"
                  breakpoint-key="lotsLivePopupCatalog"
                  :slides-per-view="5"
                  :slides-per-group="5"
                  :grid="{ rows: 2, fill: 'row' }"
                  :navigation="state.showControls"
                  :scrollbar="state.showControls"
                  :mousewheel="true"
                  :check-active-index="true"
                  @swiper-init="(swiper) => onSliderInit(swiper, tab.key)">
                  <swiper-slide
                    v-for="lot in tab.items"
                    :key="lot.id"
                    lazy="true"
                    :class="['slide-lot', { 'single-row': state.swiperParams.rows === 1 }]"
                    :data-number="lot.lot_number"
                    :data-id="lot.id"
                    @click="openLotDetailsPopup(lot)">
                    <lot-card :lot-data="lot" with-title catalog-popup></lot-card>
                  </swiper-slide>
                </swiper>
                <div v-if="tab.items?.length === 0" class="empty-result">
                  <div class="empty-result-label">
                    <template v-if="isSearchActive">
                      <div class="title">
                        {{ trans("live.catalog.empty_results_with_search_title") }} "{{
                          auctionStore.catalog.searchString
                        }}"
                      </div>
                      <div class="subtitle">{{ trans("live.catalog.empty_results_subtitle") }}</div>
                    </template>
                    <template v-else>
                      <div class="title">{{ trans("live.catalog.empty_results_title") }}</div>
                    </template>
                  </div>
                </div>
                <div
                  v-if="activeGroupLotNumbers?.length"
                  :class="['catalog-popup-lot-carousel', numPerRow, { show: state.isShowCarousel }]">
                  <div v-for="number in activeGroupLotNumbers" :key="number">{{ number || "" }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-overlay background-fill" v-show="state.isVisible" @click="close"></div>
  </teleport>
  <popup class="lot-details-popup full-width" ref="popup"></popup>
</template>

<script setup lang="ts">
  import FilterSearch from "@/components/_partials/catalog/FilterSearch.vue";
  import {
    computed,
    defineExpose,
    defineOptions,
    markRaw,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    useAttrs,
    watch
  } from "vue";
  import Popup from "@/components/_partials/Popup.vue";
  import LotDetailsPopup from "@/components/_partials/LotDetailsPopup.vue";
  import type { Swiper as SwiperSlider } from "swiper/types";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { useWindowSize } from "@vueuse/core";
  import { IActiveLot, ILot } from "@/models";
  import Swiper from "@/components/_partials/Swiper.vue";
  import { trans } from "@/utils/trans";
  import LotCard from "@/components/LotCard.vue";
  import apiClient from "@/services/api";

  const component = markRaw(LotDetailsPopup);
  const attrs = useAttrs();
  const emit = defineEmits(["close"]);
  defineOptions({
    inheritAttrs: false
  });

  const size = reactive(useWindowSize());
  const state = reactive({
    isVisible: false,
    isShowCarousel: false,
    showControls: true,
    showCarouselTimerId: null,
    customScroll: null,
    modalWrapperHeight: null,
    modalHeight: null,
    lots: [],
    isLoadLots: true,
    resizeObserver: null as ResizeObserver | null,
    swiperParams: {
      slidesPerView: 5,
      slidesPerGroup: 5,
      rows: 2
    }
  });

  const modalBlock = ref<HTMLDivElement>();
  //==================================================================================================
  const activeItem = ref<string>("catalog");
  const auctionStore = useAuctionStore();
  const popup = ref<typeof Popup>();
  const swiperMap = ref<Record<string, { swiper: SwiperSlider; slides: HTMLElement[] }>>({});
  const activeGroupLotNumbers = ref([]);

  const isSearchActive = computed(() => auctionStore.catalog.searchString?.length);

  const auctionLots = computed((): ILot[] => {
    return isSearchActive.value ? auctionStore.catalog.searchLots : auctionStore.lots;
  });

  const favoriteLots = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return auctionStore.user?.favorite_lots?.includes(i.id);
      }) || []
    );
  });

  const lotsWithUserBid = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return auctionStore.user?.bids && i.id in auctionStore.user.bids;
      }) || []
    );
  });

  const lotsNotes = computed(() => {
    return (
      auctionLots.value?.filter((i: ILot) => {
        return (auctionStore.user?.is_admin && i.has_admin_notes) || (!auctionStore.user?.is_admin && i.note?.length);
      }) || []
    );
  });

  const activeTabSlideMap = computed(() => {
    const result = {};
    const swiperData = swiperMap.value[activeItem.value];

    if (swiperData && swiperData.slides?.length) {
      swiperData.slides.forEach((slide, i: number) => {
        result[slide.dataset.id] = i;
      });
    }

    return result;
  });
  const numPerRow = computed(() => {
    return `num-per-row-${state.swiperParams?.slidesPerView}`;
  });

  watch(
    () => auctionStore.activeLot,
    (nv: IActiveLot | null) => {
      if (nv) {
        groupSlideTo(nv.id);
      }
    }
  );

  watch(
    () => size.height,
    () => {
      nextTick(() => {
        updateSwiperParams();
      });
    }
  );

  watch(
    () => size.width,
    () => {
      nextTick(() => {
        updateSwiperParams();
      });
    }
  );

  watch(
    () => auctionLots.value,
    () => {
      nextTick(() => {
        updateSwiperSlides();
      });
    }
  );

  watch(
    () => activeItem.value,
    () => {
      nextTick(() => {
        updateSwiperParams();
      });
    }
  );

  watch(
    () => state.isVisible,
    () => {
      nextTick(() => {
        updateSwiperParams();
      });
    }
  );

  const tabs = ref([
    {
      key: "catalog",
      name: "live.catalog.filters.catalog",
      items: auctionLots
    },
    ...(auctionStore.user?.id && !auctionStore.user?.is_admin
      ? [
          {
            key: "my-bids",
            name: "live.catalog.filters.my_bids",
            items: lotsWithUserBid
          }
        ]
      : []),
    ...(auctionStore.user?.id
      ? [
          {
            key: "saved-lots",
            name: "live.catalog.filters.saved_lots",
            items: favoriteLots
          }
        ]
      : []),
    ...(auctionStore.user?.id || auctionStore.user?.is_admin
      ? [
          {
            key: "notes",
            name: "live.catalog.filters.notes",
            items: lotsNotes
          }
        ]
      : [])
  ]);

  const onSliderInit = (swiper: SwiperSlider, key: string) => {
    swiperMap.value[key] = { swiper, slides: Array.from(swiper.slidesEl.querySelectorAll("swiper-slide")) };

    swiper.on("activeIndexChange", () => updateSwiperParams());
    swiper.on("slideChange", () => updateSwiperParams());
    swiper.on("breakpoint", () => updateSwiperParams());
    swiper.on("setTransition", showCarousel);
    swiper.on("sliderMove", showCarousel);
    swiper.on("scrollbarDragMove", showCarousel);

    if (key === "catalog" && swiperMap.value["catalog"]) {
      swiperMap.value["catalog"].slides.forEach((slide, i: number) => {
        if (parseInt(slide.dataset.id) === auctionStore.activeLot.id) {
          groupSlideTo(i, swiperMap.value["catalog"].swiper);
        }
      });
    }
  };

  const slideToActive = () => {
    groupSlideTo(auctionStore.activeLot.id);
  };

  const updateActiveGroupLotNumbers = () => {
    const swiperData = swiperMap.value[activeItem.value];
    const slidesPerGroup = state.swiperParams.rows * state.swiperParams.slidesPerView;

    const carouselSize = Math.min(slidesPerGroup, swiperData?.slides?.length || 0);
    const result = new Array(carouselSize);

    if (swiperData?.swiper && swiperData?.slides?.length) {
      const start = swiperData.swiper.activeIndex * state.swiperParams.rows;
      const end = start + carouselSize;
      swiperData.slides.slice(start, end).forEach((slide, i: number) => {
        result[i] = slide.dataset.number;
      });
    }

    activeGroupLotNumbers.value = result.every((i) => !i) ? null : result;
  };

  const updateSwiperSlides = () => {
    Object.keys(swiperMap.value).forEach((key) => {
      const swiper = swiperMap.value[key].swiper;
      swiperMap.value[key].slides =
        swiper.slidesEl && !swiper.slidesEl.destroyed
          ? Array.from(swiper.slidesEl.querySelectorAll("swiper-slide"))
          : [];
    });
    nextTick(() => {
      updateSwiperParams();
    });
  };

  const updateSwiperParams = () => {
    let rows = 2;
    if (size.height < 830) {
      rows = 1;
    }
    Object.entries(swiperMap.value).forEach(([key, sliderData]) => {
      if (key === activeItem.value && sliderData?.swiper?.params) {
        if (sliderData.swiper.params.grid.rows !== rows) {
          sliderData.swiper.params.grid.rows = rows;
          Object.entries(sliderData.swiper.params.breakpoints).forEach(([k, v]) => {
            v.grid = { rows: rows };
          });
        }

        const params = swiperMap.value[activeItem.value]?.swiper.params;
        const slides = swiperMap.value[activeItem.value]?.slides;
        state.showControls = slides?.length > 0 && params.slidesPerView * params.grid.rows < slides?.length;

        nextTick(() => {
          sliderData.swiper.update();
          if (state.showControls) {
            sliderData.swiper.scrollbar.enable();
          } else {
            sliderData.swiper.scrollbar.disable();
          }
        });

        state.swiperParams.slidesPerView = sliderData.swiper.params.slidesPerView;
        state.swiperParams.slidesPerGroup = sliderData.swiper.params.slidesPerGroup;
        state.swiperParams.rows = rows;
      }
    });
    nextTick(() => updateActiveGroupLotNumbers());
  };

  const showCarousel = () => {
    state.isShowCarousel = true;

    if (state.showCarouselTimerId) {
      clearTimeout(state.showCarouselTimerId);
    }

    state.showCarouselTimerId = setTimeout(() => {
      state.isShowCarousel = false;
    }, 1000);
  };

  onMounted(() => {
    activeItem.value = tabs.value[0].key;
    nextTick(() => {
      updateSwiperParams();
    });
    document.addEventListener("keydown", closeByEscapeKey);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", closeByEscapeKey);
  });

  const groupSlideTo = (lotId: number, swiper = null) => {
    if (activeItem.value) {
      swiper = swiper || swiperMap.value[activeItem.value]?.swiper;
      if (swiper) {
        const slideIndex = activeTabSlideMap.value[lotId];
        if (slideIndex >= 0) {
          swiper.slideTo(Math.floor((slideIndex / 10) * 5));
        }
      }
    }
  };

  const open = () => {
    state.isVisible = true;
    document.body.classList.add("overflow-hidden");
  };
  const close = () => {
    state.isVisible = false;
    document.body.classList.remove("overflow-hidden");
    emit("close");
  };
  const closeByEscapeKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      close();
    }
  };

  const openLotDetailsPopup = (lot: ILot) => {
    apiClient.get(`/live/lots/${lot.id}/popup-data`).then((response) => {
      popup.value?.toggleComponent(component, {
        lotProp: Object.assign(lot, response.data)
      });
      popup.value?.open();
    });
  };

  defineExpose({
    open
  });
</script>
